import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { v4 as uuidv4 } from 'uuid';

// Define page structure interfaces
export interface PageSection {
  id: string;
  title: string;
  description: string;
}

export interface Page {
  id: string;
  title: string;
  description: string;
  sections: PageSection[];
}

interface PageStructureBuilderProps {
  value: Page[];
  onChange: (pages: Page[]) => void;
}

// Simple functional component
function PageStructureBuilder(props: PageStructureBuilderProps) {
  const { value = [], onChange } = props;
  const [expandedPages, setExpandedPages] = useState<string[]>([]);
  const [expandedSection, setExpandedSection] = useState<{pageId: string, sectionId: string} | null>(null);
  
  // Log when component renders
  console.log('PageStructureBuilder rendering', { value, expandedPages });
  
  // Initialize with a homepage if no pages exist
  useEffect(() => {
    if (!value || value.length === 0) {
      const homepage: Page = {
        id: uuidv4(),
        title: 'Homepage',
        description: 'Main landing page',
        sections: []
      };
      console.log('Initializing with homepage', homepage);
      onChange([homepage]);
    }
  }, [value, onChange]);
  
  // Add a new page
  const addPage = () => {
    const newPage: Page = {
      id: uuidv4(),
      title: '',
      description: '',
      sections: []
    };
    onChange([...value, newPage]);
    
    // Set the new page as expanded by default
    setExpandedPages(prev => [...prev, newPage.id]);
  };
  
  // Delete a page
  const deletePage = (pageId: string) => {
    // Don't allow deleting the homepage
    const isHomepage = value.find(p => p.id === pageId)?.title === 'Homepage';
    if (isHomepage) {
      return;
    }
    
    const updatedPages = value.filter(page => page.id !== pageId);
    onChange(updatedPages);
    
    // Remove from expanded pages
    setExpandedPages(prev => prev.filter(id => id !== pageId));
    
    // Clear expanded section state if it was within the deleted page
    if (expandedSection?.pageId === pageId) {
      setExpandedSection(null);
    }
  };
  
  // Toggle page expansion
  const togglePageExpansion = (pageId: string) => {
    setExpandedPages(prev => {
      if (prev.includes(pageId)) {
        return prev.filter(id => id !== pageId);
      } else {
        return [...prev, pageId];
      }
    });
  };
  
  // Check if a page is expanded
  const isPageExpanded = (pageId: string) => {
    return expandedPages.includes(pageId);
  };
  
  // Add a section to a page
  const addSection = (pageId: string) => {
    const newSection: PageSection = {
      id: uuidv4(),
      title: '',
      description: ''
    };
    
    const updatedPages = value.map(page => {
      if (page.id === pageId) {
        return {
          ...page,
          sections: [...(page.sections || []), newSection]
        };
      }
      return page;
    });
    
    onChange(updatedPages);
    
    // Expand the newly added section
    setExpandedSection({
      pageId,
      sectionId: newSection.id
    });
  };
  
  // Update page title or description
  const updatePage = (pageId: string, field: 'title' | 'description', newValue: string) => {
    const updatedPages = value.map(page => 
      page.id === pageId ? { ...page, [field]: newValue } : page
    );
    onChange(updatedPages);
  };
  
  // Update section title or description
  const updateSection = (pageId: string, sectionId: string, field: 'title' | 'description', newValue: string) => {
    const updatedPages = value.map(page => {
      if (page.id === pageId) {
        return {
          ...page,
          sections: (page.sections || []).map(section => 
            section.id === sectionId ? { ...section, [field]: newValue } : section
          )
        };
      }
      return page;
    });
    onChange(updatedPages);
  };
  
  // Delete a section
  const deleteSection = (pageId: string, sectionId: string) => {
    console.log(`Deleting section ${sectionId} from page ${pageId}`);
    
    const updatedPages = value.map(page => {
      if (page.id === pageId) {
        return {
          ...page,
          sections: (page.sections || []).filter(section => section.id !== sectionId)
        };
      }
      return page;
    });
    onChange(updatedPages);
    
    // Clear expanded section state if the deleted section was expanded
    if (expandedSection?.pageId === pageId && expandedSection?.sectionId === sectionId) {
      setExpandedSection(null);
    }
  };
  
  // Toggle section expansion
  const toggleSectionExpansion = (pageId: string, sectionId: string) => {
    if (expandedSection?.pageId === pageId && expandedSection?.sectionId === sectionId) {
      setExpandedSection(null);
    } else {
      setExpandedSection({ pageId, sectionId });
    }
  };
  
  // Check if a section is expanded
  const isSectionExpanded = (pageId: string, sectionId: string) => {
    return expandedSection?.pageId === pageId && expandedSection?.sectionId === sectionId;
  };
  
  return (
    <div className="space-y-6">
      {Array.isArray(value) && value.map(page => (
        <div key={page.id} className="border rounded-md p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium">{page.title || 'Untitled Page'}</div>
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => togglePageExpansion(page.id)}
              >
                {isPageExpanded(page.id) ? 'Collapse' : 'Edit'}
              </Button>
              {/* Don't show delete button for Homepage */}
              {page.title !== 'Homepage' && (
                <Button 
                  size="sm" 
                  variant="destructive" 
                  onClick={() => deletePage(page.id)}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
          
          {isPageExpanded(page.id) && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium">Page Title</label>
                <Input 
                  value={page.title} 
                  onChange={(e) => updatePage(page.id, 'title', e.target.value)} 
                  placeholder="Enter page title"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium">Page Description</label>
                <textarea
                  value={page.description} 
                  onChange={(e) => updatePage(page.id, 'description', e.target.value)} 
                  placeholder="Enter page description"
                  className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium">Sections</label>
                
                {(!page.sections || page.sections.length === 0) ? (
                  <div className="text-sm text-gray-500 italic">No sections added yet</div>
                ) : (
                  <div className="space-y-3">
                    {page.sections.map(section => (
                      <div key={section.id} className="border rounded-md p-3 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium">{section.title || 'Untitled Section'}</div>
                          <div className="flex space-x-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => toggleSectionExpansion(page.id, section.id)}
                            >
                              {isSectionExpanded(page.id, section.id) ? 'Collapse' : 'Edit'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => deleteSection(page.id, section.id)}
                              aria-label={`Delete section ${section.title || 'Untitled Section'}`}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                        
                        {isSectionExpanded(page.id, section.id) && (
                          <div className="space-y-3 mt-2">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium">Section Title</label>
                              <Input 
                                value={section.title} 
                                onChange={(e) => updateSection(page.id, section.id, 'title', e.target.value)} 
                                placeholder="Enter section title"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <label className="block text-sm font-medium">Section Description</label>
                              <textarea
                                value={section.description} 
                                onChange={(e) => updateSection(page.id, section.id, 'description', e.target.value)} 
                                placeholder="Enter section description"
                                className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                <Button 
                  className="w-full mt-2" 
                  variant="outline"
                  onClick={() => addSection(page.id)}
                >
                  Add Section
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
      
      <Button onClick={addPage}>Add New Page</Button>
    </div>
  );
}

export default PageStructureBuilder;